import {
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
  ScatterChart, Scatter, Legend
} from "recharts";

const COLORS = ["#1d57a5", "#36a852", "#fbbc05", "#ea4335", "#8b5cf6", "#0ea5e9", "#f97316"];

// Convert { columns, rows } → [{ col1: val, col2: val }, ...]
const normalizeData = (data) => {
  if (!data) return [];
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && !Array.isArray(data[0])) return data;
  const columns = data.columns || data.Columns;
  const rows = data.rows || data.Rows;
  if (columns && rows) {
    return rows.map(row =>
      Object.fromEntries(columns.map((col, i) => [col, row[i]]))
    );
  }
  return [];
};

const formatYAxis = (val) => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
  return val;
};

export default function DynamicChartRenderer({ spec, data, darkMode }) {
  if (!spec || !data) return null;
  
  // let { type, Type, title, Title, xKey, XKey, yKeys, YKeys, yKey, YKey } = spec;
  const type = spec.type?.toLowerCase() || spec.Type?.toLowerCase();
  const title = spec.title || spec.Title;
  const xKey = spec.xKey || spec.XKey;
  const yKeys = spec.yKeys || spec.YKeys;
  const yKey = spec.yKey || spec.YKey;
  const keys = yKeys || (yKey ? [(yKey)] : []);
  const rawData = normalizeData(data);

  if (!rawData.length || !keys.length) return null;

  // Detect extra columns that are not xKey and not yKeys — treat as groupKey
  const allColumns = data.columns || data.Columns || [];
  const groupKey = allColumns.find(col => col !== xKey && !keys.includes(col)) || null;

  // If groupKey exists, pivot data: xKey on x-axis, each unique groupKey value becomes a Bar/Line
  let chartData = rawData;
  let dynamicKeys = keys;

  if (groupKey) {
    const groupValues = [...new Set(rawData.map(r => r[groupKey]))];
    const xValues = [...new Set(rawData.map(r => r[xKey]))];
    chartData = xValues.map(xVal => {
      const entry = { [xKey]: xVal };
      groupValues.forEach(gVal => {
        const match = rawData.find(r => r[xKey] === xVal && r[groupKey] === gVal);
        entry[gVal] = match ? match[keys[0]] : 0;
      });
      return entry;
    });
    dynamicKeys = groupValues;
  }

  if (!chartData.length || !keys.length) return null;
  const isDark = darkMode;
  const ChartWrapper = ({ children }) => (
    <div className="w-full mt-2 p-3 dark:bg-slate-800 bg-white border border-slate-100 dark:border-slate-900 rounded-lg">
      {title && <h3 className="text-sm font-semibold text-slate-800 dark:text-white mb-2">{title}</h3>}
      <div className="w-full h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );

  if (type === "bar") {
    return (
      <ChartWrapper>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 10, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#d5d4d4" : "#e2e8f0"} />

          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11, fill: isDark ? "#fff" : "#000" }}
            angle={-35}
            textAnchor="end"
            interval={0}
          />

          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 11, fill: isDark ? "#fff" : "#000" }}
          />

          <Tooltip formatter={(val) => val.toLocaleString()} />
          <Legend />
          {dynamicKeys.map((k, i) => (
            <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ChartWrapper>
    );
  }

  if (type === "line") {
    return (
      <ChartWrapper>
        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#d5d4d4" : "#e2e8f0"} />
          <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: isDark ? "#fff" : "#000" }} padding={{ left: 40, right: 40 }} />
          <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: isDark ? "#fff" : "#000" }} />
          <Tooltip 
            formatter={(val) => val.toLocaleString()} 
            contentStyle={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              border: "none",
              color: isDark ? "#fff" : "#000",
            }}
           />
          <Legend wrapperStyle={{ color: isDark ? "#fff" : "#000" }}/>
          {dynamicKeys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 4 }} />
          ))}
        </LineChart>
      </ChartWrapper>
    );
  }

  if (type === "pie" || type === "donut") {
    const categories = groupKey ? [...new Set(rawData.map(r => r[groupKey]))] : [xKey];
    const xValues = [...new Set(rawData.map(r => r[xKey]))];

    const allPieData = groupKey
      ? categories.map(gVal => ({
          label: gVal,
          data: xValues.map(xVal => {
            const match = rawData.find(r => r[groupKey] === gVal && r[xKey] === xVal);
            return { name: xVal, value: Number(match?.[keys[0]] || 0) };
          }),
        }))
      : [{ label: null, data: rawData.map(r => ({ name: r[xKey], value: Number(r[keys[0]] || 0) })) }];

    return (
      <div className="w-full mt-2 p-3 dark:bg-slate-800 bg-white border border-slate-100 dark:border-slate-900 rounded-lg">
        {title && <h3 className="text-sm font-semibold text-slate-800 dark:text-white mb-2">{title}</h3>}
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(allPieData.length, 4)}, 1fr)` }}>
          {allPieData.map(({ label, data }, pi) => (
            <div key={pi} className="flex flex-col items-center">
              {label && <p className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1 text-center w-full truncate">{label}</p>}
              <div className="w-full" style={{ height: '180px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={type === "donut" ? 40 : 0} outerRadius={70}>
                      {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(val) => Number(val).toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Per-pie legend */}
              <div className="flex flex-col gap-1 mt-1 w-full px-1 overflow-y-auto" style={{ maxHeight: `${5 * 22}px` }}>
                {data.map((entry, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="truncate">{entry.name || entry.Name}</span>
                    <span className="ml-auto font-medium text-slate-800 dark:text-white whitespace-nowrap">
                      {Number(entry.value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "scatter") {
    const scatterGroups = groupKey
      ? [...new Set(rawData.map(r => r[groupKey]))].map(gVal => ({
          name: gVal,
          data: rawData.filter(r => r[groupKey] === gVal).map(r => ({ x: r[xKey], y: r[keys[0]] })),
        }))
      : [{ name: keys[0], data: rawData.map(r => ({ x: r[xKey], y: r[keys[0]] })) }];

    return (
      <ChartWrapper>
        <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
          <CartesianGrid stroke={isDark ? "#d5d4d4" : "#e2e8f0"} />
          <XAxis dataKey="x" type="category" allowDuplicatedCategory={false} tick={{ fontSize: 11, fill: isDark ? "#fff" : "#000" }} angle={-35} textAnchor="end" interval={0} />
          <YAxis dataKey="y" tickFormatter={formatYAxis} tick={{ fontSize: 11, fill: isDark ? "#fff" : "#000" }} />
          <Tooltip 
            formatter={(val) => val.toLocaleString()}
            contentStyle={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              border: "none",
              color: isDark ? "#fff" : "#000",
            }}
          />
          <Legend wrapperStyle={{ color: isDark ? "#fff" : "#000" }} />
          {scatterGroups.map((group, i) => (
            <Scatter key={group.name} name={group.name} data={group.data} fill={COLORS[i % COLORS.length]} />
          ))}
        </ScatterChart>
      </ChartWrapper>
    );
  }

  return null;
}
